import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { sendEditorialMessage } from '@generated/server/editorial';

const PAPERS = ['Red Hook Star-Revue', 'The Village Star-Revue', 'Either / Both'] as const;
type Paper = (typeof PAPERS)[number];
const input = 'h-11 w-full border border-input bg-background px-3 text-sm';
const label = 'text-xs font-semibold uppercase tracking-wider';

export function LetterToEditor() {
  const [paper, setPaper] = useState<Paper>('Either / Both');
  const [letter, setLetter] = useState('');
  const [name, setName] = useState('');
  const [hood, setHood] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [withhold, setWithhold] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const words = letter.trim() ? letter.trim().split(/\s+/).length : 0;
  const ready = letter.trim().length >= 20 && name.trim().length > 1 && (email.trim().length > 3 || phone.trim().length > 6);

  const send = async () => {
    setSending(true); setErr(null);
    try {
      await sendEditorialMessage({ data: { type: 'Letter to the Editor', paper, message: withhold ? `PLEASE DO NOT PRINT MY NAME OR NEIGHBORHOOD.\n\n${letter}` : letter, anonymous: false, senderName: name, neighborhood: hood, email, phone } });
      setSent(true); window.scrollTo({ top: 0 });
    } catch (e) { console.error(e); setErr(e instanceof Error ? e.message : 'Your letter could not be sent.'); }
    finally { setSending(false); }
  };

  if (sent) return (
    <section className="mx-auto max-w-2xl border-y-4 border-double border-foreground py-10 text-center">
      <Check className="mx-auto h-10 w-10 text-[hsl(var(--brand-green))]" />
      <h2 className="mt-3 font-[family-name:var(--font-heading)] text-3xl font-semibold">Thank you for your letter.</h2>
      <p className="mt-3 text-sm text-muted-foreground">It went straight to the editor. If we plan to print it, we may contact you to confirm.</p>
      <button onClick={() => { setSent(false); setLetter(''); }} className="mt-6 min-h-11 bg-foreground px-6 text-sm font-semibold text-background">Write another letter</button>
    </section>
  );

  return (
    <section className="grid gap-8 lg:grid-cols-[300px_1fr]">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--brand-green))]">Letters</div>
        <h2 className="mt-2 font-[family-name:var(--font-heading)] text-3xl font-semibold leading-tight text-[hsl(var(--brand-green))]">Letter to the Editor</h2>
        <p className="mt-3 text-sm text-muted-foreground">Have your say about something in the paper or in the neighborhood. We will print your name and neighborhood with your letter unless you ask us not to. We may edit for length and clarity.</p>
        <p className="mt-3 text-sm text-muted-foreground">Your email and phone are only used to confirm your letter and are never printed.</p>
      </div>
      <div className="min-w-0 space-y-6">
        <fieldset><legend className={label}>Which paper?</legend>
          <div className="mt-2 flex flex-wrap gap-2">{PAPERS.map((p) => (
            <button key={p} type="button" aria-pressed={paper === p} onClick={() => setPaper(p)} className={`min-h-11 border px-4 text-sm ${paper === p ? 'border-[hsl(var(--brand-green))] bg-[hsl(var(--brand-green))] text-primary-foreground' : 'border-border bg-card'}`}>{p}</button>))}</div>
        </fieldset>
        <div>
          <div className="flex items-baseline justify-between"><label htmlFor="lt-body" className={label}>Your letter</label><span className={`text-xs ${words > 400 ? 'text-destructive' : 'text-muted-foreground'}`}>{words} words{words > 400 ? ' (letters over 400 words may be shortened)' : ''}</span></div>
          <textarea id="lt-body" value={letter} onChange={(e) => setLetter(e.target.value)} className="mt-2 min-h-56 w-full border border-input bg-background p-3 font-[family-name:var(--font-heading)] text-base" placeholder="To the Editor," />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label htmlFor="lt-name" className="text-sm">Your name</label><input id="lt-name" value={name} onChange={(e) => setName(e.target.value)} className={input} /></div>
          <div><label htmlFor="lt-hood" className="text-sm">Neighborhood</label><input id="lt-hood" value={hood} onChange={(e) => setHood(e.target.value)} placeholder="e.g. Red Hook" className={input} /></div>
          <div><label htmlFor="lt-email" className="text-sm">Email</label><input id="lt-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} /></div>
          <div><label htmlFor="lt-phone" className="text-sm">Phone</label><input id="lt-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={input} /></div>
          <label className="flex min-h-11 items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={withhold} onChange={(e) => setWithhold(e.target.checked)} className="h-5 w-5" />Please don't print my name and neighborhood with this letter.</label>
          <p className="text-xs text-muted-foreground sm:col-span-2">Your name plus an email or phone number are needed so we can confirm the letter is yours.</p>
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <button onClick={() => void send()} disabled={!ready || sending} className="inline-flex min-h-11 items-center gap-2 bg-[hsl(var(--brand-green))] px-6 text-sm font-semibold text-primary-foreground disabled:opacity-50">{sending && <Loader2 className="h-4 w-4 animate-spin" />}Send my letter</button>
      </div>
    </section>
  );
}
